import { useEffect, useRef, forwardRef } from "react";
import { images } from "../constants";

const TechnicalSkills = () => {
  const performanceRef = useRef(null);
  const skillsRef = useRef(null);
  const innovationRef = useRef(null);
  const developmentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const circle = entry.target.querySelector(".progress-circle");
            const percent = entry.target.getAttribute("data-percent");
            animateCircle(circle, percent);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (performanceRef.current) observer.observe(performanceRef.current);
    if (skillsRef.current) observer.observe(skillsRef.current);
    if (innovationRef.current) observer.observe(innovationRef.current);
    if (developmentRef.current) observer.observe(developmentRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const animateCircle = (circle, percent) => {
    const circumference = 2 * Math.PI * 40;
    const offset = circumference - (percent / 100) * circumference;

    circle.style.strokeDasharray = `${circumference} ${circumference}`;
    circle.style.strokeDashoffset = circumference;

    setTimeout(() => {
      circle.style.transition = "stroke-dashoffset 1.5s ease-in-out";
      circle.style.strokeDashoffset = offset;
    }, 100);
  };

  // ✅ Now using forwardRef
  const SkillItem = forwardRef(({ percent, title, description }, ref) => (
    <div
      ref={ref}
      data-percent={percent}
      style={{ backgroundImage: `url(${images.bg})` }}
      className="flex flex-col items-center p-6 m-4 bg-white dark:bg-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
    >
      <div className="relative w-32 h-32 mb-4">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          <circle
            className="text-gray-200 dark:text-gray-600"
            strokeWidth="8"
            stroke="currentColor"
            fill="transparent"
            r="40"
            cx="50"
            cy="50"
          />
          <circle
            className="progress-circle text-blue-600 dark:text-blue-400"
            strokeWidth="8"
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r="40"
            cx="50"
            cy="50"
            style={{ strokeDashoffset: 251.2 }}
          />
        </svg>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-gray-300 dark:text-white">
          {percent}%
        </div>
      </div>
      <h3 className="text-xl font-semibold text-gray-300 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-gray-300 dark:text-gray-300 text-center text-sm">
        {description}
      </p>
    </div>
  ));

  return (
    <div className="py-5 px-4 sm:px-6 lg:px-8 dark:#030000">
      <h2 className="text-3xl sm:text-5xl lg:text-6xl text-center my-5 lg:my-7 bg-gradient-to-r from-blue-500 to-orange-800 text-transparent bg-clip-text">
        TECHNICAL SKILLS
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SkillItem
          ref={performanceRef}
          percent="100"
          title="PERFORMANCE"
          description="Visiga Seminary maintains a 100% pass rate in final exams, reflecting the dedication of both students and staff to academic excellence and discipline."
        />

        <SkillItem
          ref={skillsRef}
          percent="75"
          title="SKILLS"
          description="Seminarians develop 75% proficiency in key skill areas such as leadership, farming, sports, and other disciplines supporting holistic formation."
        />

        <SkillItem
          ref={innovationRef}
          percent="85"
          title="INNOVATION"
          description="85% of school activities involve students directly, fostering creativity and leadership through participation in various programs."
        />

        <SkillItem
          ref={developmentRef}
          percent="95"
          title="DEVELOPMENT"
          description="95% focus is placed on nurturing students' physical and spiritual growth through structured programs and daily practices."
        />
      </div>
    </div>
  );
};

export default TechnicalSkills;
